<agr-dialog [data]="data" (cancel)="close()">
  <ng-template #detailsHeaderTemplate>
    <div class="details-header">
      <div class="date-navigation">
        <button
          mat-icon-button
          class="details-navigation-button"
          [disabled]="!canGoToPreviousObservation()"
          [attr.aria-label]="'TGA.Notifications.FieldObservationDialog.PreviousObservation' | translate"
          (click)="onPreviousObservation()"
        >
          <mat-icon>chevron_left</mat-icon>
        </button>

        <div class="date-content">
          <div class="date-label">
            <mat-icon>calendar_today</mat-icon>
            <span>{{ currentObservation().observationDate | date: 'd MMMM yyyy' : undefined : locale() }}</span>
          </div>
        </div>

        <button
          mat-icon-button
          class="details-navigation-button"
          [disabled]="!canGoToNextObservation()"
          [attr.aria-label]="'TGA.Notifications.FieldObservationDialog.NextObservation' | translate"
          (click)="onNextObservation()"
        >
          <mat-icon>chevron_right</mat-icon>
        </button>
      </div>
    </div>
  </ng-template>

  <ng-template #mediaPanelTemplate>
    @if (!isCompactLayout() && hasCurrentImages()) {
      <div class="zoom-controls">
        <button
          mat-icon-button
          class="floating-button"
          [disabled]="!canZoomIn()"
          [attr.aria-label]="'TGA.Notifications.FieldObservationDialog.ZoomIn' | translate"
          (click)="onZoomIn()"
        >
          <mat-icon>zoom_in</mat-icon>
        </button>
        <button
          mat-icon-button
          class="floating-button"
          [disabled]="!canZoomOut()"
          [attr.aria-label]="'TGA.Notifications.FieldObservationDialog.ZoomOut' | translate"
          (click)="onZoomOut()"
        >
          <mat-icon>zoom_out</mat-icon>
        </button>
        @if (isZoomedIn()) {
          <button
            mat-icon-button
            class="floating-button"
            [attr.aria-label]="'TGA.Notifications.FieldObservationDialog.ResetZoom' | translate"
            (click)="onResetZoom()"
          >
            <mat-icon>refresh</mat-icon>
          </button>
        }
      </div>
    }

    <div
      #imageStage
      class="image-stage"
      (wheel)="onImageWheel($event)"
      (pointermove)="onImagePointerMove($event)"
      (pointerup)="onImagePointerUp($event)"
      (pointercancel)="onImagePointerUp($event)"
    >
      @if (hasCurrentImages()) {
        <button
          mat-icon-button
          class="floating-button image-navigation image-navigation--previous"
          [disabled]="!canGoToPreviousImage()"
          [attr.aria-label]="'TGA.Notifications.FieldObservationDialog.PreviousImage' | translate"
          (click)="onPreviousImage()"
        >
          <mat-icon>chevron_left</mat-icon>
        </button>
      }

      @if (currentImageSrc(); as currentImageSrc) {
        <img
          #mainImage
          class="main-image"
          [class.main-image--draggable]="isZoomedIn()"
          [class.main-image--dragging]="isDraggingImage"
          [src]="currentImageSrc"
          [alt]="currentAltText()"
          [style.transform]="imageTransform()"
          draggable="false"
          (load)="onImageLoad()"
          (pointerdown)="onImagePointerDown($event)"
        />
      } @else {
        <div class="empty-state">
          <mat-icon>image_not_supported</mat-icon>
          <p>{{ 'TGA.Notifications.FieldObservationDialog.NoImages' | translate }}</p>
        </div>
      }

      @if (showNextImageButton() && hasCurrentImages()) {
        <button
          mat-icon-button
          class="floating-button image-navigation image-navigation--next"
          [disabled]="!canGoToNextImage()"
          [attr.aria-label]="'TGA.Notifications.FieldObservationDialog.NextImage' | translate"
          (click)="onNextImage()"
        >
          <mat-icon>chevron_right</mat-icon>
        </button>
      }

      @if (hasCurrentImages()) {
        <div class="image-counter">{{ imageCounter() }}</div>
      }

      @if (isCompactLayout() && hasCurrentImages()) {
        <div class="mobile-image-controls">
          <div
            class="mobile-image-controls__menu"
            [class.mobile-image-controls__menu--open]="isMobileImageControlsOpen()"
          >
            <button
              mat-icon-button
              class="floating-button mobile-image-controls__action"
              [disabled]="!canZoomIn()"
              [attr.aria-label]="'TGA.Notifications.FieldObservationDialog.ZoomIn' | translate"
              (click)="onZoomIn()"
            >
              <mat-icon>zoom_in</mat-icon>
            </button>
            <button
              mat-icon-button
              class="floating-button mobile-image-controls__action"
              [disabled]="!canZoomOut()"
              [attr.aria-label]="'TGA.Notifications.FieldObservationDialog.ZoomOut' | translate"
              (click)="onZoomOut()"
            >
              <mat-icon>zoom_out</mat-icon>
            </button>
            <button
              mat-icon-button
              class="floating-button mobile-image-controls__action"
              [disabled]="!isZoomedIn()"
              [attr.aria-label]="'TGA.Notifications.FieldObservationDialog.ResetZoom' | translate"
              (click)="onResetZoom()"
            >
              <mat-icon>refresh</mat-icon>
            </button>
          </div>

          <button
            mat-icon-button
            class="floating-button mobile-image-controls__toggle"
            [attr.aria-label]="'TGA.Notifications.FieldObservationDialog.ImageControls' | translate"
            (click)="toggleMobileImageControls()"
          >
            <mat-icon>{{ isMobileImageControlsOpen() ? 'close' : 'tune' }}</mat-icon>
          </button>
        </div>
      }
    </div>

    @if (hasCurrentImages()) {
      <div class="thumbnail-strip">
        @for (image of currentImages(); track imageTrackKey(image, $index)) {
          <button
            class="thumbnail-button"
            [class.active]="$index === currentImageIndex()"
            [attr.aria-label]="
              'TGA.Notifications.FieldObservationDialog.SelectImage' | translate: selectImageTranslationParams($index)
            "
            (click)="onSelectImage($index)"
          >
            @if (thumbnailSrc(image); as thumbnailSrc) {
              <img [src]="thumbnailSrc" alt="" class="thumbnail-image" />
            } @else {
              <span class="thumbnail-placeholder">
                <mat-icon>image</mat-icon>
              </span>
            }
          </button>
        }
      </div>
    }
  </ng-template>

  <ng-template #observationDetailsTemplate>
    <section class="details-section">
      <h2 class="section-title">{{ 'TGA.Notifications.FieldObservationDialog.Remarks' | translate }}</h2>
      <div class="remarks-card">
        <p>
          {{ currentObservation().remarks || ('TGA.Notifications.FieldObservationDialog.NoRemarks' | translate) }}
        </p>
      </div>
    </section>

    <section class="details-section">
      <h2 class="section-title">{{ 'TGA.Notifications.FieldObservationDialog.Observations' | translate }}</h2>

      @if (currentObservation().observations.length) {
        <div class="observations-card">
          @for (observationSection of currentObservation().observations; track observationSection.category) {
            <section class="observation-section">
              <div class="observation-section-header">
                <mat-icon>{{ observationIcon(observationSection.category) }}</mat-icon>
                <span>{{ observationSection.category | translate }}</span>
              </div>

              <div class="observation-items">
                @for (item of observationSection.items; track item) {
                  @if (shouldTranslateObservationItems(observationSection.category)) {
                    <p>{{ item | translate }}</p>
                  } @else {
                    <p>{{ item }}</p>
                  }
                }
              </div>
            </section>
          }
        </div>
      } @else {
        <div class="empty-observations">
          <mat-icon>assignment</mat-icon>
          <p>{{ 'TGA.Notifications.FieldObservationDialog.NoObservations' | translate }}</p>
        </div>
      }
    </section>
  </ng-template>

  @if (isCompactLayout()) {
    <div class="dialog-shell-mobile">
      <ng-container [ngTemplateOutlet]="detailsHeaderTemplate"></ng-container>

      <mat-tab-group
        class="mobile-tab-group"
        [selectedIndex]="selectedMobileTab()"
        (selectedIndexChange)="onMobileTabChange($event)"
        animationDuration="0ms"
      >
        <mat-tab [label]="'TGA.Notifications.FieldObservationDialog.PhotosTab' | translate">
          <div class="mobile-tab-panel">
            <section class="media-panel media-panel--mobile">
              <ng-container [ngTemplateOutlet]="mediaPanelTemplate"></ng-container>
            </section>
          </div>
        </mat-tab>

        <mat-tab [label]="'TGA.Notifications.FieldObservationDialog.ObservationTab' | translate">
          <div class="mobile-tab-panel mobile-tab-panel--details">
            <div class="details-content details-content--mobile">
              <ng-container [ngTemplateOutlet]="observationDetailsTemplate"></ng-container>
            </div>
          </div>
        </mat-tab>
      </mat-tab-group>
    </div>
  } @else {
    <div class="dialog-shell dialog-shell--desktop">
      <section class="media-panel">
        <ng-container [ngTemplateOutlet]="mediaPanelTemplate"></ng-container>
      </section>

      <aside class="details-panel">
        <ng-container [ngTemplateOutlet]="detailsHeaderTemplate"></ng-container>

        <div class="details-content">
          <ng-container [ngTemplateOutlet]="observationDetailsTemplate"></ng-container>
        </div>
      </aside>
    </div>
  }
</agr-dialog>
